import { useState } from "react";
import type { ContentType, VoteDirection } from "../../api/types";
import type { CommentNode as CommentNodeData } from "../../lib/comments";
import { voteComment } from "../../api/content";
import { timeAgo } from "../../lib/format";
import { CommentForm } from "./CommentForm";

/** Visual nesting cap; deeper replies hide behind a "continue thread" link. */
const MAX_DEPTH = 5;
/** Comments at or below this score start collapsed (downvoted into hiding). */
const LOW_SCORE = -3;

interface CommentNodeProps {
  node: CommentNodeData;
  contentType: ContentType;
  sourceId: number;
  depth: number;
  /** Author name treated as the original poster, badged "OP". */
  opName?: string;
  /** Refetch the whole thread after a reply is posted. */
  onReplyPosted: () => void;
}

export function CommentNode({
  node,
  contentType,
  sourceId,
  depth,
  opName,
  onReplyPosted,
}: CommentNodeProps) {
  const { comment, children, descendantCount } = node;

  const [collapsed, setCollapsed] = useState(comment.score <= LOW_SCORE);
  const [replying, setReplying] = useState(false);
  const [continued, setContinued] = useState(false);

  // Local, optimistic vote state (no per-user dedupe server-side; this just
  // reflects the click and prevents double-voting within the session).
  const [voted, setVoted] = useState<VoteDirection | null>(null);
  const [upvotes, setUpvotes] = useState(comment.upvotes);
  const [downvotes, setDownvotes] = useState(comment.downvotes);
  const [voting, setVoting] = useState(false);
  const score = upvotes - downvotes;

  async function cast(direction: VoteDirection) {
    if (voted || voting || comment.deleted) return;
    setVoting(true);
    // optimistic
    if (direction === "UP") setUpvotes((n) => n + 1);
    else setDownvotes((n) => n + 1);
    setVoted(direction);
    try {
      const updated = await voteComment(comment.id, direction);
      setUpvotes(updated.upvotes);
      setDownvotes(updated.downvotes);
    } catch {
      // revert on failure
      if (direction === "UP") setUpvotes((n) => n - 1);
      else setDownvotes((n) => n - 1);
      setVoted(null);
    } finally {
      setVoting(false);
    }
  }

  const isOp = !!opName && comment.authorName === opName && !comment.deleted;
  const replyCount = descendantCount;

  if (collapsed) {
    return (
      <div className="comment comment--collapsed">
        <div className="comment__main">
          <button
            className="comment__toggle"
            onClick={() => setCollapsed(false)}
            aria-label="Expand comment"
          >
            [+]
          </button>{" "}
          <span className="comment__collapsed">
            <strong>{comment.authorName}</strong> · {score} point
            {score === 1 ? "" : "s"}
            {replyCount > 0 ? ` · ${replyCount} repl${replyCount === 1 ? "y" : "ies"}` : ""}
            {comment.score <= LOW_SCORE ? " · below threshold" : ""}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`comment${comment.deleted ? " comment--deleted" : ""}`}>
      {!comment.deleted && (
        <div className="comment__votes">
          <button
            className="vote-btn"
            data-on={voted === "UP" ? "up" : undefined}
            onClick={() => cast("UP")}
            disabled={!!voted || voting}
            aria-label="Upvote"
            title="Upvote"
          >
            ▲
          </button>
          <span className="vote-score" data-neg={score < 0 ? "true" : undefined}>
            {score}
          </span>
          <button
            className="vote-btn"
            data-on={voted === "DOWN" ? "down" : undefined}
            onClick={() => cast("DOWN")}
            disabled={!!voted || voting}
            aria-label="Downvote"
            title="Downvote"
          >
            ▼
          </button>
        </div>
      )}

      <div className="comment__main">
        <div className="comment__head">
          <button
            className="comment__toggle"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse comment"
          >
            [−]
          </button>
          <span className={`comment__author${isOp ? " comment__author--op" : ""}`}>
            {comment.authorName}
            {isOp ? " · OP" : ""}
          </span>
          <span>{timeAgo(comment.createdAt)}</span>
        </div>

        <div className="comment__body">{comment.body}</div>

        {!comment.deleted && (
          <div className="comment__actions">
            <button
              className="comment__action"
              onClick={() => setReplying((r) => !r)}
            >
              {replying ? "Close" : "Reply"}
            </button>
          </div>
        )}

        {replying && (
          <CommentForm
            contentType={contentType}
            sourceId={sourceId}
            parentId={comment.id}
            compact
            onCancel={() => setReplying(false)}
            onPosted={() => {
              setReplying(false);
              onReplyPosted();
            }}
          />
        )}

        {children.length > 0 &&
          (depth + 1 >= MAX_DEPTH && !continued ? (
            <button
              className="comment__continue comment__action"
              onClick={() => setContinued(true)}
            >
              continue this thread ({replyCount} more) →
            </button>
          ) : (
            <div className="comment__children">
              {children.map((child) => (
                <CommentNode
                  key={child.comment.id}
                  node={child}
                  contentType={contentType}
                  sourceId={sourceId}
                  // Reset depth when "continue" is clicked so the revealed
                  // sub-thread can nest a few more levels before capping again.
                  depth={continued ? 0 : depth + 1}
                  opName={opName}
                  onReplyPosted={onReplyPosted}
                />
              ))}
            </div>
          ))}
      </div>
    </div>
  );
}
